import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
// Removed secure storage import - using localStorage directly for consent
import { analytics } from '../services/analytics';

export const CookieConsent: React.FC = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      setShowBanner(true);
    }
  }, []);

  const handleAcceptAll = () => {
    // Update consent in storage
    localStorage.setItem('cookie_consent', 'all');
    localStorage.setItem('analytics_consent', 'granted');
    
    // Update Google Analytics consent
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted',
        ad_storage: 'denied' // We don't use ads
      });
    }
    
    // Update analytics service
    analytics.updateConsent({
      analytics: 'granted',
      ads: 'denied',
    });
    
    setShowBanner(false);
  };

  const handleAcceptNecessary = () => {
    // Update consent in storage
    localStorage.setItem('cookie_consent', 'necessary');
    localStorage.setItem('analytics_consent', 'denied');
    
    // Update Google Analytics consent
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
        ad_storage: 'denied'
      });
    }
    
    // Update analytics service
    analytics.updateConsent({
      analytics: 'denied',
      ads: 'denied',
    });
    
    setShowBanner(false);
  };

  const handleManageCookies = () => {
    setShowDetails(!showDetails);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-black/20 backdrop-blur-sm">
      <Card className="max-w-4xl mx-auto p-6">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">
            {t('cookieConsent.title', 'Cookie Preferences')}
          </h3>
          
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('cookieConsent.description', 
              'We use cookies and similar technologies to help personalize content, analyze traffic, and improve your experience. By accepting, you consent to our use of cookies.'
            )}
          </p>

          {showDetails && (
            <div className="space-y-3 border-t pt-3">
              <div>
                <h4 className="font-medium text-sm">
                  {t('cookieConsent.necessary', 'Necessary Cookies')}
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('cookieConsent.necessaryDesc', 
                    'Required for the website to function properly. These cannot be disabled.'
                  )}
                </p>
              </div>
              
              <div>
                <h4 className="font-medium text-sm">
                  {t('cookieConsent.analytics', 'Analytics Cookies')}
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('cookieConsent.analyticsDesc', 
                    'Help us understand how visitors interact with our website by collecting anonymous information.'
                  )}
                </p>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2 justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleManageCookies}
            >
              {showDetails 
                ? t('cookieConsent.hideDetails', 'Hide Details')
                : t('cookieConsent.manageCookies', 'Manage Cookies')
              }
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleAcceptNecessary}
            >
              {t('cookieConsent.acceptNecessary', 'Accept Necessary Only')}
            </Button>
            
            <Button
              variant="primary"
              size="sm"
              onClick={handleAcceptAll}
            >
              {t('cookieConsent.acceptAll', 'Accept All')}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};